// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { outExpo } from '../core/ease.ts';
import { clamp, smoothstep } from '../core/math.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { Camera } from '../three.ts';
import { roundRect } from '../type.ts';
type Card = {
    x: number;
    y: number;
    z0: number;
    speed: number;
    rot: number;
    spin: number;
    kind: number;
};
/** Documents, chat bubbles and data cards fly at the camera while key words slam in on the beat. */
export default defineScene({
    id: 'card-storm',
    version: '0.1.0',
    aspects: ['16:9'],
    propsSchema: z.object({ words: z.array(z.string().check(z.maxLength(24))).check(z.minLength(1)).check(z.maxLength(4)) }),
    glyphs: (p) => p.words.join(''),
    motion: () => 1,
    draw(ctx, props, env) {
        const { colors } = env.brand;
        ctx.fillStyle = colors.ink;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const r = env.rng('cards');
        const cards: Card[] = Array.from({ length: 36 }, () => ({
            x: (r() - 0.5) * 2600,
            y: (r() - 0.5) * 1500,
            z0: 900 + r() * 5200,
            speed: 1500 + r() * 1400,
            rot: (r() - 0.5) * 0.8,
            spin: (r() - 0.5) * 0.9,
            kind: Math.floor(r() * 3),
        }));
        const cam = new Camera({ fov: 62 });
        const placed = cards
            .map((c) => {
            const depth = 6100;
            const z = ((((c.z0 - c.speed * env.local) % depth) + depth) % depth) + 60;
            return { c, z, p: cam.project([c.x, c.y, z]) };
        })
            .filter((e) => e.p)
            .sort((a, b) => b.z - a.z);
        for (const { c, z, p } of placed) {
            if (!p)
                continue;
            const fadeIn = smoothstep(6200, 4200, z);
            const fadeOut = smoothstep(80, 420, z);
            const alpha = fadeIn * fadeOut * clamp(env.local * 3);
            if (alpha <= 0.01)
                continue;
            const w = 320 * p.scale;
            const h = (c.kind === 1 ? 150 : 210) * p.scale;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(c.rot + c.spin * env.local);
            ctx.globalAlpha = alpha;
            roundRect(ctx, -w / 2, -h / 2, w, h, 18 * p.scale);
            ctx.fillStyle = c.kind === 1 ? rgba(colors.primary, 0.9) : rgba(colors.paper, 0.1);
            ctx.fill();
            ctx.lineWidth = Math.max(1, 2 * p.scale);
            ctx.strokeStyle = rgba(c.kind === 2 ? colors.accent : colors.paper, 0.55);
            ctx.stroke();
            ctx.fillStyle = rgba(colors.paper, 0.6);
            if (c.kind === 2) {
                for (let i = 0; i < 6; i++) {
                    const bh = h * (0.2 + 0.6 * Math.abs(Math.sin(i * 1.7 + c.x)));
                    ctx.fillRect(-w / 2 + w * (0.12 + i * 0.13), h / 2 - h * 0.12 - bh * 0.7, w * 0.08, bh * 0.7);
                }
            }
            else {
                for (let i = 0; i < (c.kind === 1 ? 2 : 5); i++)
                    ctx.fillRect(-w / 2 + w * 0.1, -h / 2 + h * (0.2 + i * 0.15), w * (i % 2 ? 0.55 : 0.75), 8 * p.scale);
            }
            ctx.restore();
        }
        // Words: one per beat, starting on beat 1 of the scene.
        const index = Math.floor(env.localBeat) - 1;
        const word = props.words[index];
        if (index >= 0 && word) {
            const k = outExpo(clamp((env.localBeat - (index + 1)) * 4));
            const scale = 1.7 - 0.7 * k;
            ctx.save();
            ctx.translate(DESIGN_W / 2, DESIGN_H / 2);
            ctx.scale(scale, scale);
            ctx.globalAlpha = k;
            ctx.font = font(env.brand, 800, 190);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = colors.paper;
            ctx.shadowColor = rgba(colors.accent, 0.8);
            ctx.shadowBlur = 40;
            ctx.fillText(word, 0, 0);
            const w = ctx.measureText(word).width * scale;
            ctx.restore();
            env.report?.text(`word-${index}`, [DESIGN_W / 2 - w / 2, DESIGN_H / 2 - 95 * scale, w, 190 * scale], k);
        }
    },
});
